import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddVehicleTypeForm from "./useAddVehicleTypeForm";

export const addVehicleTypePageRouteName =
  "/master-data/vehicle-type/add";

const AddVehicleTypePage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    config,
    categoryOptions,
    tempTypeOptions,
  } = useAddVehicleTypeForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Vehicle Category */}
            <Controller
              name="categoryId"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-vehicle-category")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={categoryOptions}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="select-vehicle-category"
                  />
                </div>
              )}
            />

            {/* Vehicle Type Name */}
            <Input
              label={t("label-vehicle-type")}
              placeholder={t("label-vehicle-type")}
              required
              {...register("vehicleType")}
              error={errors.vehicleType}
              data-testid="input-vehicle-type"
            />

            {/* Temp Option - Conditional: when category has_temp_type */}
            {config.hasTempType && (
              <Controller
                name="tempTypeId"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-temp-option")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={tempTypeOptions}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      error={fieldState.error}
                      placeholder={t("label-temp-option")}
                      data-testid="dropdown-temp-option"
                    />
                  </div>
                )}
              />
            )}
            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-vehicle-type-status"
                  />
                </div>
              )}
            />
             {/* Is Chassis Toggle - Conditional: when category has_chassis */}
            {config.hasChassis && (
              <Controller
                name="isChassis"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-is-chassis")} <span className="text-muted">(optional)</span>
                    </Typography>
                    <Typography variant="body" tone="muted">
                      {t("is-chassis-description")}
                    </Typography>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={field.value}
                      onClick={() => field.onChange(!field.value)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        field.value ? "bg-primary-600" : "bg-greyScale-60"
                      }`}
                      data-testid="toggle-is-chassis"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          field.value ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                )}
              />
            )}
             {/* Is Container Toggle - Conditional: when category has_container */}
            {config.hasContainer && (
              <Controller
                name="isContainer"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between">
                    <Typography variant="bodyLarge">
                      {t("label-is-container")}
                    </Typography>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={field.value}
                      onClick={() => field.onChange(!field.value)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        field.value ? "bg-primary-600" : "bg-greyScale-60"
                      }`}
                      data-testid="toggle-is-container"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          field.value ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                )}
              />
            )}

            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-type"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-type"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-to-list-type"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {fields.map((item, index) => {
                const categoryOption = categoryOptions.find(c => c.value === item.categoryId);
                const tempTypeOption = tempTypeOptions.find(t => t.value === item.tempTypeId);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between rounded-xl border p-4 ${
                      editingIndex === index
                        ? "border-primary-500 bg-primary-50"
                        : "border-border"
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <Typography variant="bodyLarge" className="font-semibold">
                        {item.vehicleType}
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Typography variant="body" tone="muted">
                          {t("label-vehicle-category")}
                        </Typography>
                        <Typography variant="body" tone="muted">
                          :
                        </Typography>
                        <Typography
                          variant="body"
                          className="capitalize"
                        >
                          {categoryOption?.label ?? item.categoryId}
                        </Typography>
                      </div>
                      {categoryOption?.hasTempType && item.tempTypeId && (
                        <div className="flex items-center gap-2">
                          <Typography variant="body" tone="muted">
                            {t("label-temp-option")}
                          </Typography>
                          <Typography variant="body" tone="muted">
                            :
                          </Typography>
                          <Typography variant="body">
                            {tempTypeOption?.label ?? item.tempTypeId}
                          </Typography>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Typography variant="body" tone="muted">
                          {t("label-status")}
                        </Typography>
                        <Typography variant="body" tone="muted">
                          :
                        </Typography>
                        <Typography
                          variant="body"
                          className="capitalize"
                        >
                          {item.status}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditItem(index)}
                        className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                          editingIndex === index
                            ? "bg-primary-500 text-white"
                            : "hover:bg-greyScale-90 text-primary-500"
                        }`}
                        data-testid={`btn-edit-row-type-${index}`}
                      >
                        <Icon name="edit-2" size={16} />
                      </button>
                      {fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(index)}
                          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer"
                          data-testid={`btn-delete-row-type-${index}`}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-type"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-type"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-type-modal"
      />
    </div>
  );
};

export default AddVehicleTypePage;
